import { useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { OUTCOME_ANSWERS, OUTCOME_QUESTIONS, OutcomeCheckinAnswersSchema, type CheckinState, type OutcomeCheckinAnswers, type OutcomeQuestion } from "@pushback-and-chaos/contracts";
import { ProblemJsonError } from "@pushback-and-chaos/client";
import { BRAND, CHECK_IN } from "../design/copy.js";
import { BloomBackground } from "../ui/background.js";
import { Pill, Rule } from "../ui/primitives.js";

/**
 * The two requests the page makes, named here rather than picked off
 * PushbackClient (2026-10-11): the page is built beside the client methods,
 * and a shape that drifts fails at the one place the real client is handed
 * in (app.tsx), not in every test that renders the form with a fake.
 */
export interface CheckInClient {
  readonly getCheckin: (token: string) => Promise<CheckinState>;
  readonly postCheckin: (token: string, answers: OutcomeCheckinAnswers) => Promise<unknown>;
}

/** A question's copy with its answers as a plain lookup, so the contract's keys can index it. */
export function questionCopy(question: OutcomeQuestion): { readonly question: string; readonly answers: Readonly<Record<string, string>> } {
  return CHECK_IN.questions[question];
}

/** A question's answer keys, in Codex's order (the contract's tuple, widened for iteration). */
export function answersOf(question: OutcomeQuestion): readonly string[] {
  return OUTCOME_ANSWERS[question];
}

type Phase = "form" | "sending" | "saved" | "closedNow" | "failed";

/** A refusal the server gives a link it does not verify: the page says so; anything else is the server not answering. */
function isBadLink(error: unknown): boolean {
  return error instanceof ProblemJsonError && [400, 401, 403, 404, 410].includes(error.status);
}

/**
 * THE OUTCOME CHECK-IN (`#/check-in?token=`, CME stage 3b, 2026-10-11): the
 * page the email after recruiting season links to. Reached cold, outside the
 * shell, with no identity: the signed token in the hash is the whole claim,
 * and the server verifies it on both requests (packages/mail's
 * `verifyMailLink`; the token is opaque here).
 *
 * Codex's section 4 verbatim: the heading, the four single-choice questions
 * in order with nothing selected in advance and no free text, the privacy
 * note under the form, "send answers." and the thank-you line. The form is
 * open only while the server says so (the OUTCOME_CHECKIN choice holds and
 * no answer exists for the season); closed, it shows one sentence and no
 * form. Code decides what may be sent: the four answers are parsed by the
 * contract's schema before the one request, so an incomplete form cannot
 * be sent.
 *
 * Same card-on-bloom composition as the privacy page, which is the one other
 * page a learner is handed from outside the product.
 */
export function CheckInPage({ client, token }: { client: CheckInClient; token: string | null }): ReactNode {
  const state = useQuery({
    queryKey: ["check-in", token],
    queryFn: () => client.getCheckin(token ?? ""),
    enabled: token !== null,
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
  });
  const [answers, setAnswers] = useState<Partial<Record<OutcomeQuestion, string>>>({});
  const [phase, setPhase] = useState<Phase>("form");
  const parsed = OutcomeCheckinAnswersSchema.safeParse(answers);

  const send = async (): Promise<void> => {
    if (token === null || !parsed.success || phase === "sending") return;
    setPhase("sending");
    try {
      await client.postCheckin(token, parsed.data);
      setPhase("saved");
    } catch (error) {
      if (error instanceof ProblemJsonError && error.status === 409) {
        // Closed between the read and the send (answered elsewhere, or the
        // choice withdrawn): ask the server why, so the sentence is its reason.
        await state.refetch();
        setPhase("closedNow");
      } else {
        setPhase("failed");
      }
    }
  };

  const body = ((): ReactNode => {
    if (token === null) return <Line>{CHECK_IN.badLink}</Line>;
    if (state.isPending) return null;
    if (state.isError) return <Line>{isBadLink(state.error) ? CHECK_IN.badLink : CHECK_IN.notLoaded}</Line>;
    if (!state.data.open) return <Line>{CHECK_IN.closed[state.data.reason]}</Line>;
    if (phase === "saved") return <Line role="status">{CHECK_IN.thankYou}</Line>;
    if (phase === "closedNow") return <Line>{CHECK_IN.closedNow}</Line>;
    return (
      <>
        <form
          data-testid="check-in-form"
          onSubmit={(event) => {
            event.preventDefault();
            void send();
          }}
        >
          {OUTCOME_QUESTIONS.map((question) => {
            const copy = questionCopy(question);
            return (
              <fieldset key={question} data-question={question} className="m-0 mt-8 min-w-0 border-0 p-0">
                <legend className="m-0 p-0 font-sans text-body text-ink">{copy.question}</legend>
                {answersOf(question).map((answer) => (
                  <label key={answer} className="mt-2 flex cursor-pointer items-center gap-3 font-sans text-body text-ink">
                    <input
                      type="radio"
                      name={question}
                      value={answer}
                      checked={answers[question] === answer}
                      disabled={phase === "sending"}
                      onChange={() => setAnswers((previous) => ({ ...previous, [question]: answer }))}
                    />
                    <span>{copy.answers[answer] ?? answer}</span>
                  </label>
                ))}
              </fieldset>
            );
          })}
          <div className="mt-8">
            <Pill type="submit" disabled={!parsed.success || phase === "sending"}>
              {CHECK_IN.submit}
            </Pill>
          </div>
          {phase === "failed" ? (
            <p role="status" className="mt-4 mb-0 font-sans text-small">
              {CHECK_IN.notSaved}
            </p>
          ) : null}
        </form>
        <p className="mt-8 mb-0 font-sans text-small text-ink-soft">{CHECK_IN.privacyNote}</p>
      </>
    );
  })();

  return (
    <div className="relative flex min-h-svh items-start justify-center sm:py-16">
      <BloomBackground />
      <div data-testid="check-in-card" className="relative z-10 w-full bg-paper px-6 py-10 sm:w-[44%] sm:min-w-[34rem] sm:max-w-[46rem] sm:px-16 sm:py-12">
        <div className="font-sans text-nav">{BRAND.short}</div>
        <Rule className="mt-2" />
        <h1 className="mt-6 mb-0 font-display text-display font-bold">{CHECK_IN.heading}</h1>
        {body}
      </div>
    </div>
  );
}

function Line({ children, role }: { children: ReactNode; role?: "status" | undefined }): ReactNode {
  return (
    <p role={role} className="mt-8 mb-0 font-sans text-body">
      {children}
    </p>
  );
}
